import { useMemo } from "react";

import useRouter from "./useRouter";
import useTranslate from "./useTranslate";
import { ROUTES } from "../_constants/routes";

// Resolves the static table's i18n keys into display names. Keys an entry omits come out
// `undefined`, which every consumer already guards on — `routes.holidays` is a namespace
// with no page and no url of its own.
const resolveRoutes = (i18n: (key: string) => string) =>
  ROUTES.reduce<Record<string, any>>((resolved, route) => {
    const resolvedRoute: Record<string, any> = {
      url: route.url,
      name: route.labelKey && i18n(route.labelKey),
      singularName: route.singularKey && i18n(route.singularKey),
    };

    (route.actions || []).forEach(action => {
      resolvedRoute[action.key] = {
        url: action.url,
        name: i18n(action.labelKey),
        query: action.query,
      };
    });

    resolved[route.key] = resolvedRoute;
    return resolved;
  }, {});

// `i18n` only changes identity when translations load or the language changes
// (react-i18next hands back a `t` it holds in state), so this memo is what gives `routes`
// a stable identity — it used to be manufactured by a JSON round-trip on every render.
const useRoutes = () => {
  const { location } = useRouter();
  const i18n = useTranslate();
  const { pathname } = location;

  const routes = useMemo(() => resolveRoutes(i18n), [i18n]);

  return {
    routes,
    currentRoute: routes[pathname === "/" ? "root" : pathname.replace("/", "")],
  };
};

export default useRoutes;
